import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  FiltersButton,
  Button,
  FilterBottomSheet,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SortableHeadCell,
  ToolbarSearch,
  ViewToggle,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { useVoices, useDeleteVoice } from '../hooks';
import { voicesApi } from '../services/voicesApi';
import { VoiceCard, type VoiceCardAction } from '../components/VoiceCard';
import { VoiceListItem } from '../components/VoiceListItem';
import type { VoiceQuery, VoiceResponse, VoiceStatus } from '@troia-v3/types';
import { toast } from 'sonner';

type ViewMode = 'grid' | 'table';
type SortKey = 'name' | 'status';

const STATUS_OPTIONS: Array<{ value: VoiceStatus | undefined; label: string }> = [
  { value: undefined, label: 'Todas' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
  { value: 'processing', label: 'Processando' },
  { value: 'failed', label: 'Falhou' },
];

export const VoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();

  // "+" central do bottom-nav mobile → novo item (mockup .m-fab/.m-add).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); navigate('/automation/voices/new'); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [navigate]);
  const queryClient = useQueryClient();

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<VoiceQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data, isLoading, error } = useVoices(query);
  const deleteVoice = useDeleteVoice();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Seleção
  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((v) => selectedIds.has(v.id));
  const someOnPageSelected = items.some((v) => selectedIds.has(v.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((v) => next.delete(v.id));
      else items.forEach((v) => next.add(v.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  // Busca (live) — filtra por nome/descrição no backend
  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  // Filtros (Status) — suportado pela VoiceQuery/backend
  const setStatusFilter = (status?: VoiceStatus) => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };
  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, page: 1 }));
    setFiltersOpen(false);
  };
  const statusLabel = query.status ? (STATUS_OPTIONS.find((o) => o.value === query.status)?.label ?? null) : null;
  const activeFilterCount = statusLabel ? 1 : 0;

  // Ordenação — server-side (backend aceita sortBy: name|status)
  const handleSort = (key: SortKey) =>
    setQuery((prev) => ({
      ...prev,
      sortBy: key,
      sortOrder: prev.sortBy === key && prev.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));

  // Ações
  const invalidateVoices = () => {
    queryClient.invalidateQueries({ queryKey: ['voices'] });
    queryClient.invalidateQueries({ queryKey: ['voices-dropdown'] });
  };

  const handleDelete = async (voiceId: string) => {
    if (await confirm({ message: 'Deseja realmente excluir esta voz?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
      try {
        await deleteVoice.mutateAsync(voiceId);
      } catch {
        // Erro tratado pelo hook (toast)
      }
    }
  };

  const handleToggleStatus = async (voiceId: string, currentStatus: string) => {
    try {
      const newStatus: VoiceStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await voicesApi.update(voiceId, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Voz ativada!' : 'Voz desativada!');
      invalidateVoices();
    } catch {
      toast.error('Erro ao alterar status da voz');
    }
  };

  const handleVoiceAction = (action: VoiceCardAction, voice: VoiceResponse) => {
    switch (action) {
      case 'edit':
        navigate(`/automation/voices/${voice.id}/edit`);
        break;
      case 'delete':
        handleDelete(voice.id);
        break;
      case 'toggleStatus':
        handleToggleStatus(voice.id, voice.status);
        break;
    }
  };

  // Ações em lote — mutations single em loop
  const bulkDeactivate = async () => {
    const targets = items.filter((v) => selectedIds.has(v.id) && v.status === 'active');
    if (targets.length === 0) {
      clearSelection();
      return;
    }
    try {
      await Promise.all(targets.map((v) => voicesApi.update(v.id, { status: 'inactive' })));
      toast.success('Vozes desativadas!');
    } catch {
      toast.error('Erro ao desativar vozes');
    } finally {
      invalidateVoices();
      clearSelection();
    }
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} voz(es) selecionada(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteVoice.mutate(id));
    clearSelection();
  };

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {STATUS_OPTIONS.map((opt) => {
        const active = query.status === opt.value;
        return (
          <button
            key={opt.label}
            onClick={() => setStatusFilter(opt.value)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {opt.label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          /* Esquerda: breadcrumb + contador */
          <>
          <nav className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Automação
            </span>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Vozes</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-22) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar voz…"
            shrinkClass="ds-search"
            data-testid="voices-search"
          />
        }
        right={
          /* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */
          <>
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="voices-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="voices-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="voices-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Nova voz (único primário — F01-03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => navigate('/automation/voices/new')}
            data-testid="new-voice"
          >
            Nova voz
          </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar voz…"
              data-testid="voices-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="voices-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="voices-filters-clear">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }} data-testid="voices-bulk-bar">
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionada(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="voices-bulk-deactivate">
            <Icon name="power" className="w-3.5 h-3.5" /> Desativar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="voices-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="voices-bulk-cancel">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar vozes: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhuma voz encontrada"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Clone vozes para usar com seus agentes de IA'}
              actionLabel="Nova voz"
              onAction={() => navigate('/automation/voices/new')}
            />
          )}

          {/* Grid (Cards) — F34: sem checkbox de seleção em cards */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((voice: VoiceResponse) => (
                <VoiceCard
                  key={voice.id}
                  voice={voice}
                  onAction={handleVoiceAction}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards (F34: sem checkbox) */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {items.map((voice: VoiceResponse) => (
                  <VoiceCard
                    key={voice.id}
                    voice={voice}
                    onAction={handleVoiceAction}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    {/* F43: head padrão shared (SortableHeadCell, densidade px-4 py-3) */}
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        data-testid="voices-check-all"
                      />
                      <SortableHeadCell label="Nome" sortKey="name" activeKey={query.sortBy as SortKey | undefined} dir={query.sortOrder ?? 'asc'} onSort={handleSort} data-testid="voices-sort-name" />
                      <PlainHeadCell label="Provider" />
                      <SortableHeadCell label="Status" sortKey="status" activeKey={query.sortBy as SortKey | undefined} dir={query.sortOrder ?? 'asc'} onSort={handleSort} data-testid="voices-sort-status" />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((voice: VoiceResponse) => (
                      <VoiceListItem
                        key={voice.id}
                        voice={voice}
                        selected={selectedIds.has(voice.id)}
                        onToggleSelect={toggleSelect}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação (F48 — padrão completo "Mostrando X–Y de N") */}
          {!isLoading && !error && data != null && data.total > 0 && (
            <ListPagePagination
              currentPage={data.page ?? 1}
              totalPages={data.totalPages ?? 1}
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
              totalItems={data.total}
              pageSize={query.limit ?? 12}
              itemLabel="vozes"
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="voices-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>
    </div>
  );
};
